import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  DenyQualityApprovalMutation,
  QualityApprovalDenyFailure,
  QualityApprovalDenyInput,
} from "@/graphql/codegen/graphql"
import { DenyQualityApprovalDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseDenyQualityApprovalReturn {
  denyQualityApproval: (input: QualityApprovalDenyInput) => Promise<boolean>
  isDenying: boolean
}

export const useDenyQualityApproval = (): UseDenyQualityApprovalReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<DenyQualityApprovalMutation, { input: QualityApprovalDenyInput }>(
    DenyQualityApprovalDocument
  )

  const denyQualityApproval = useCallback(
    async (input: QualityApprovalDenyInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.qualityApproval.deny
      const hasError = error || output?.__typename === "QualityApprovalDenyFailure"

      if (hasError) {
        const message = error?.message ?? (output as QualityApprovalDenyFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      notify({ type: NotificationType.Success, message: "Out of spec approval request denied" })

      return true
    },
    [executeMutation, notify]
  )

  return { denyQualityApproval, isDenying: fetching }
}
